import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  CheckCircle, Package, Truck, Calendar, 
  Clock, Gift, CreditCard, Home, ShoppingBag
} from 'lucide-react';

const OrderConfirmation = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get order from session storage
    const savedOrder = sessionStorage.getItem('lastOrder');
    if (savedOrder) {
      setOrder(JSON.parse(savedOrder));
    }
    setLoading(false);
  }, [orderId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-pink-50 flex items-center justify-center py-8 px-4">
        <div className="text-center max-w-sm">
          <Package className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-3 text-gray-900">Order Not Found</h2>
          <p className="text-gray-600 text-sm mb-6">Unable to find order details</p>
          <Link 
            to="/shop"
            className="inline-flex items-center justify-center bg-pink-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-pink-700"
          >
            <ShoppingBag className="h-4 w-4 mr-2" />
            Continue Shopping
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-pink-50 py-8">
      <div className="container mx-auto px-4">
        {/* Success Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Order Confirmed!</h1>
          <p className="text-gray-600">Thank you for your purchase</p>
          <div className="mt-4 inline-block bg-pink-100 text-pink-800 px-4 py-2 rounded-full text-sm font-medium">
            Order ID: {order.orderId}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Order Details */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
              <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                <Package className="h-5 w-5 text-pink-600" />
                Order Summary
              </h2>
              
              <div className="space-y-4">
                {order.items.map((item, index) => (
                  <div key={index} className="flex justify-between items-center py-3 border-b last:border-0">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gray-100 rounded-lg overflow-hidden">
                        {item.image && (
                          <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                        )}
                      </div>
                      <div>
                        <div className="font-medium text-sm">{item.name}</div>
                        <div className="text-xs text-gray-500">
                          Quantity: {item.quantity} • ₹{item.price} each
                        </div>
                        {item.customMessage && (
                          <div className="text-xs text-blue-600 mt-1">
                            Message: "{item.customMessage}"
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="font-semibold">₹{item.price * item.quantity}</div>
                  </div>
                ))}
              </div>

              {/* Price Breakdown */}
              <div className="mt-6 pt-6 border-t">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Subtotal</span>
                    <span>₹{order.summary.subtotal}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Shipping</span>
                    <span>{order.summary.shipping === 0 ? 'FREE' : `₹${order.summary.shipping}`}</span>
                  </div>
                  {order.delivery?.timeSlotCharge > 0 && (
                    <div className="flex justify-between text-yellow-600">
                      <span>Time Slot Charge</span>
                      <span>+₹{order.delivery.timeSlotCharge}</span>
                    </div>
                  )}
                  <div className="border-t pt-3 flex justify-between text-lg font-bold">
                    <span>Total</span>
                    <span className="text-pink-600">₹{order.summary.total}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Delivery & Payment Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="font-bold mb-4 flex items-center gap-2">
                  <Truck className="h-5 w-5 text-pink-600" />
                  Delivery Details
                </h3>
                <div className="space-y-3">
                  <div>
                    <div className="text-sm text-gray-500">Delivery To</div>
                    <div className="font-medium">{order.customer.name}</div>
                    <div className="text-sm text-gray-600">{order.customer.address}</div>
                    <div className="text-sm text-gray-600">
                      {order.customer.city}, {order.customer.pincode}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Phone</div>
                    <div className="font-medium">{order.customer.phone}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Email</div>
                    <div className="font-medium">{order.customer.email}</div>
                  </div>
                  {order.customer.deliveryDate && (
                    <div>
                      <div className="text-sm text-gray-500">Delivery Schedule</div>
                      <div className="font-medium">
                        {new Date(order.customer.deliveryDate).toLocaleDateString()} • {order.customer.deliveryTime}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="font-bold mb-4 flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-pink-600" />
                  Payment Details
                </h3>
                <div className="space-y-3">
                  <div>
                    <div className="text-sm text-gray-500">Payment Method</div>
                    <div className="font-medium">
                      {order.paymentMethod === 'cod' ? 'Cash on Delivery' : 
                       order.paymentMethod === 'card' ? 'Credit/Debit Card' : 'UPI'}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Order Status</div>
                    <div className="font-medium text-green-600 capitalize">{order.status}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Order Date</div>
                    <div className="font-medium">
                      {new Date(order.createdAt).toLocaleDateString()} • {new Date(order.createdAt).toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Next Steps */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm p-6 sticky top-8">
              <h3 className="font-bold mb-6">What's Next?</h3>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-pink-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Calendar className="h-4 w-4 text-pink-600" />
                  </div>
                  <div>
                    <div className="font-medium text-sm">Delivery Scheduled</div>
                    <div className="text-xs text-gray-600 mt-1">
                      Your order will be delivered as per your selected time slot
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Gift className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <div className="font-medium text-sm">Gift Message Included</div>
                    <div className="text-xs text-gray-600 mt-1">
                      {order.message ? `"${order.message}"` : 'No gift message added'}
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Truck className="h-4 w-4 text-green-600" />
                  </div>
                  <div>
                    <div className="font-medium text-sm">Track Your Order</div>
                    <div className="text-xs text-gray-600 mt-1">
                      You will receive tracking information via SMS & Email
                    </div>
                  </div>
                </div>
              </div>

              {/* Order Notes */}
              <div className="mt-8 pt-6 border-t">
                <h4 className="font-medium mb-3">Important Notes</h4>
                <ul className="space-y-2 text-xs text-gray-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-3 w-3 text-green-500 mt-0.5" />
                    <span>7-day freshness guarantee on all flowers</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-3 w-3 text-green-500 mt-0.5" />
                    <span>Free gift wrapping included</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-3 w-3 text-green-500 mt-0.5" />
                    <span>Contact us for any changes to delivery</span>
                  </li>
                </ul>
              </div>

              {/* Action Buttons */}
              <div className="mt-8 space-y-3">
                <Link 
                  to="/shop"
                  className="block w-full bg-pink-600 text-white text-center py-3 rounded-lg font-medium hover:bg-pink-700"
                >
                  Continue Shopping
                </Link>
                <Link 
                  to="/"
                  className="block w-full border border-gray-300 text-gray-700 text-center py-3 rounded-lg font-medium hover:bg-gray-50"
                >
                  <Home className="inline-block h-4 w-4 mr-2" />
                  Back to Home
                </Link>
              </div>

              {/* Contact Support */}
              <div className="mt-6 pt-6 border-t">
                <div className="text-center">
                  <div className="text-sm text-gray-500 mb-1">Need help?</div>
                  <div className="font-medium text-pink-600">📞 1800-123-4567</div>
                  <div className="text-xs text-gray-500 mt-1">We're here 24/7</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmation;